import React, { useEffect, useMemo, useRef, useState } from "react";
import { IMaskInput } from "react-imask";
import { isValid, parse } from "date-fns";
import { COLUMN_BY_KEY } from "../../constants/expandedColumns";
import {
  AMOUNT_UNITS,
  formatAmount,
  operationsJournalMock,
} from "../../mock/operationsJournalMock";
import { AnalyticsCell, SortIcon, getSideAnalytics } from "../tableParts";
import "./CorrectionsTable.css";

const LEADING_COLUMNS = [
  { key: "date", label: "Дата", type: "date" },
  { key: "organization", label: "Организация" },
];

const TRAILING_COLUMNS = [
  { key: "quantity", label: "Количество", type: "number", sortable: true },
  { key: "amount", label: "Сумма", type: "amount", sortable: true },
  { key: "budgetVersion", label: "Версия бюджета", sortable: true },
  { key: "correctionComment", label: "Комментарий к корректировке" },
];

const pluralizeFields = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} поле`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return `${count} поля`;
  }
  return `${count} полей`;
};

const isValidDate = (text) =>
  /^\d{2}\.\d{2}\.\d{4}$/.test(text) &&
  isValid(parse(text, "dd.MM.yyyy", new Date()));

/** Значение из инпута → значение корректировки; undefined — ввод некорректный, не сохраняем */
const parseInputValue = (text, type) => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (type === "number" || type === "amount") {
    const number = Number(trimmed.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(number) ? number : undefined;
  }
  if (type === "date") return isValidDate(trimmed) ? trimmed : undefined;
  return trimmed;
};

const formatCellValue = (value, type, unit) => {
  if (value == null) return null;
  if (type === "amount") return formatAmount(value, unit);
  if (type === "number") return Number(value).toLocaleString("ru-RU");
  return value;
};

function EditableCell({
  value,
  column,
  unit,
  isDirty,
  listId,
  className,
  onCommit,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState("");
  const isCancelledRef = useRef(false);

  const startEditing = () => {
    isCancelledRef.current = false;
    setText(value == null ? "" : String(value));
    setIsEditing(true);
  };

  const finishEditing = () => {
    setIsEditing(false);
    if (isCancelledRef.current) return;
    const parsed = parseInputValue(text, column.type);
    if (parsed !== undefined) onCommit(parsed);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") event.currentTarget.blur();
    if (event.key === "Escape") {
      isCancelledRef.current = true;
      event.currentTarget.blur();
    }
  };

  const tdClassName = [
    "ojr-editable",
    className,
    isDirty && "is-dirty",
    (column.type === "number" || column.type === "amount") && "is-numeric",
  ]
    .filter(Boolean)
    .join(" ");

  if (isEditing) {
    const inputProps = {
      className: "ojr-input",
      autoFocus: true,
      onBlur: finishEditing,
      onKeyDown: handleKeyDown,
    };
    return (
      <td className={`${tdClassName} is-editing`}>
        {column.type === "date" ? (
          <IMaskInput
            {...inputProps}
            mask="00.00.0000"
            value={text}
            unmask={false}
            placeholder="ДД.ММ.ГГГГ"
            onAccept={(next) => setText(next)}
          />
        ) : (
          <input
            {...inputProps}
            type="text"
            inputMode={
              column.type === "number" || column.type === "amount"
                ? "decimal"
                : undefined
            }
            list={listId}
            value={text}
            placeholder={column.type === "amount" ? "Сумма в ₸" : undefined}
            onChange={(event) => setText(event.target.value)}
          />
        )}
      </td>
    );
  }

  const display = formatCellValue(value, column.type, unit);

  return (
    <td className={tdClassName}>
      <button
        type="button"
        className="ojr-cell"
        title="Нажмите, чтобы изменить"
        onClick={startEditing}
      >
        <span className={display == null ? "oj-empty" : "ojr-cell__value"}>
          {display ?? "—"}
        </span>
        <svg
          className="ojr-cell__pencil"
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden
        >
          <path
            d="M11.5 2.5L13.5 4.5L5.5 12.5L2.5 13.5L3.5 10.5L11.5 2.5Z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </td>
  );
}

function CorrectionsTable({
  rows,
  pageStart,
  viewMode,
  columnOrder,
  unit,
  editor,
  authorName,
  selectedId,
  onOpenOperation,
  onSwitchToExpanded,
}) {
  const isExpanded = viewMode === "expanded";
  const amountUnitLabel = AMOUNT_UNITS[unit].label;

  const editableColumns = useMemo(
    () => [
      ...LEADING_COLUMNS,
      ...columnOrder.map((key) => COLUMN_BY_KEY[key]),
      ...TRAILING_COLUMNS,
    ],
    [columnOrder]
  );

  /** Подсказки для текстовых полей — значения, которые уже встречаются в данных */
  const suggestions = useMemo(() => {
    const result = {};
    editableColumns
      .filter((column) => !column.type || column.type === "link")
      .forEach((column) => {
        const values = [
          ...new Set(
            operationsJournalMock
              .map((row) => row[column.key])
              .filter((value) => value != null && value !== "")
          ),
        ];
        if (values.length) result[column.key] = values;
      });
    return result;
  }, [editableColumns]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handleKeyDown = (event) => {
      const target = event.target;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;
      if (!(event.metaKey || event.ctrlKey)) return;

      const key = event.key.toLowerCase();
      if (key === "z" && !event.shiftKey) {
        event.preventDefault();
        editor.undo();
      } else if ((key === "z" && event.shiftKey) || key === "y") {
        event.preventDefault();
        editor.redo();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isExpanded, editor]);

  const getRowClassName = (row) => {
    const classes = ["oj-row"];
    if (editor.getRowCorrection(row.id) || editor.hasRowChanges(row.id)) {
      classes.push("ojr-row--corrected");
    }
    if (selectedId === row.id) classes.push("is-selected");
    if (isExpanded) classes.push("ojr-row--editable");
    return classes.join(" ");
  };

  const renderHeaderLabel = (column) => (
    <>
      {column.type === "amount"
        ? `${column.label} (${amountUnitLabel})`
        : column.label}
      {column.sortable && (
        <>
          {" "}
          <SortIcon />
        </>
      )}
    </>
  );

  const renderFixedCells = (row, index) => (
    <>
      <td>{pageStart + index + 1}</td>
      <td>{row.date}</td>
      <td>
        <span className="oj-doc">
          <button
            type="button"
            className="oj-link"
            onClick={(event) => {
              event.stopPropagation();
              onOpenOperation(row);
            }}
          >
            {row.document}
          </button>
          {row.hasWarning && (
            <span className="oj-doc__warning" title="Требует внимания" />
          )}
        </span>
      </td>
      <td>{row.organization}</td>
    </>
  );

  const renderFixedHeader = () => (
    <>
      <th>№</th>
      <th>
        <span className="ojr-th-multiline">
          Дата
          <br />
          (исх)
        </span>{" "}
        <SortIcon />
      </th>
      <th>Документ</th>
      <th>
        <span className="ojr-th-multiline">
          Организация
          <br />
          (исх)
        </span>{" "}
        <SortIcon />
      </th>
    </>
  );

  const renderMetaCells = (row) => {
    const correction = editor.getRowCorrection(row.id);
    const fieldsCount = correction ? Object.keys(correction.fields).length : 0;
    return (
      <>
        <td>
          {fieldsCount ? (
            <span className="ojr-badge">{pluralizeFields(fieldsCount)}</span>
          ) : (
            <span className="oj-empty">—</span>
          )}
        </td>
        <td>
          {correction ? (
            <div className="oj-stack">
              <div>{correction.author}</div>
              <div className="oj-stack__secondary">{correction.date}</div>
            </div>
          ) : (
            <span className="oj-empty">—</span>
          )}
        </td>
      </>
    );
  };

  return (
    <>
      {isExpanded ? (
        <div className="ojr-toolbar">
          <div className="ojr-toolbar__history">
            <button
              type="button"
              className="ojr-icon-btn"
              aria-label="Отменить (Ctrl+Z)"
              title="Отменить (Ctrl+Z)"
              disabled={!editor.canUndo}
              onClick={editor.undo}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M5.5 3L2.5 6L5.5 9M3 6H10C11.933 6 13.5 7.567 13.5 9.5S11.933 13 10 13H7"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="ojr-icon-btn"
              aria-label="Повторить (Ctrl+Shift+Z)"
              title="Повторить (Ctrl+Shift+Z)"
              disabled={!editor.canRedo}
              onClick={editor.redo}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10.5 3L13.5 6L10.5 9M13 6H6C4.067 6 2.5 7.567 2.5 9.5S4.067 13 6 13H9"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <span className="ojr-toolbar__divider" />
          <span
            className={`ojr-toolbar__status${
              editor.changesCount ? " has-changes" : ""
            }`}
          >
            {editor.changesCount
              ? `Несохранённых изменений: ${editor.changesCount}`
              : "Нет несохранённых изменений"}
          </span>
          <div className="ojr-toolbar__actions">
            {editor.changesCount > 0 && (
              <button
                type="button"
                className="ojr-toolbar__discard"
                onClick={editor.discard}
              >
                Отменить все
              </button>
            )}
            <button
              type="button"
              className="ojr-toolbar__save"
              disabled={!editor.changesCount}
              onClick={() => editor.save(authorName)}
            >
              Сохранить изменения
            </button>
          </div>
        </div>
      ) : (
        <div className="ojr-notice">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
            <path
              d="M8 7.5V11M8 5V5.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          Для редактирования корректировок{" "}
          <button
            type="button"
            className="ojr-notice__link"
            onClick={onSwitchToExpanded}
          >
            перейдите в расширенный режим
          </button>
        </div>
      )}

      <div className="oj-table-scroll">
        {isExpanded ? (
          <table className="oj-table ojr-table ojr-table--expanded">
            <thead>
              <tr>
                {renderFixedHeader()}
                {editableColumns.map((column, index) => (
                  <th
                    key={column.key}
                    className={index === 0 ? "ojr-divider" : undefined}
                  >
                    {renderHeaderLabel(column)}
                  </th>
                ))}
                <th>Изменено</th>
                <th>Изменил</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id} className={getRowClassName(row)}>
                  {renderFixedCells(row, index)}
                  {editableColumns.map((column, columnIndex) => (
                    <EditableCell
                      key={column.key}
                      column={column}
                      unit={unit}
                      value={editor.getValue(row.id, column.key)}
                      isDirty={editor.isDirty(row.id, column.key)}
                      listId={
                        suggestions[column.key]
                          ? `ojr-suggestions-${column.key}`
                          : undefined
                      }
                      onCommit={(value) =>
                        editor.setValue(row.id, column.key, value)
                      }
                      className={columnIndex === 0 ? "ojr-divider" : undefined}
                    />
                  ))}
                  {renderMetaCells(row)}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="oj-table ojr-table ojr-table--compact">
            <thead>
              <tr>
                {renderFixedHeader()}
                <th className="ojr-divider">Дата</th>
                <th>Организация</th>
                <th>Аналитики Дт</th>
                <th>Аналитики Кт</th>
                {TRAILING_COLUMNS.map((column) => (
                  <th key={column.key}>{renderHeaderLabel(column)}</th>
                ))}
                <th>Изменено</th>
                <th>Изменил</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const fields = editor.getEffectiveFields(row.id);
                return (
                  <tr
                    key={row.id}
                    className={getRowClassName(row)}
                    onClick={() => onOpenOperation(row)}
                  >
                    {renderFixedCells(row, index)}
                    <td className="ojr-divider">
                      {fields.date ?? <span className="oj-empty">—</span>}
                    </td>
                    <td>
                      {fields.organization ?? <span className="oj-empty">—</span>}
                    </td>
                    <td className="ojr-analytics">
                      <AnalyticsCell items={getSideAnalytics(fields, "Dt")} />
                    </td>
                    <td className="ojr-analytics">
                      <AnalyticsCell items={getSideAnalytics(fields, "Kt")} />
                    </td>
                    {TRAILING_COLUMNS.map((column) => {
                      const display = formatCellValue(
                        fields[column.key],
                        column.type,
                        unit
                      );
                      const isNumeric =
                        column.type === "number" || column.type === "amount";
                      return (
                        <td
                          key={column.key}
                          className={
                            isNumeric ? "oj-cell--num oj-cell--amount" : undefined
                          }
                        >
                          {display ?? <span className="oj-empty">—</span>}
                        </td>
                      );
                    })}
                    {renderMetaCells(row)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {isExpanded &&
        Object.entries(suggestions).map(([key, values]) => (
          <datalist key={key} id={`ojr-suggestions-${key}`}>
            {values.map((value) => (
              <option key={value} value={value} />
            ))}
          </datalist>
        ))}
    </>
  );
}

export default CorrectionsTable;
